import type { DochatAuth, DochatAuthProvider, DochatDisplayConfig } from "@do/shared-types";
import { Button } from "@do/ui/components/button";
import { CSSVariables } from "@do/ui-chat-v2/components/css-variables";
import { Fonts } from "@do/ui-chat-v2/components/fonts";
import type { ReactNode } from "react";
import { getAuthTexts } from "../../auth/texts";
import { useSignIn } from "../../auth/useSignIn";

type LoginScreenProps = {
  dochatConfigId: string;
  config: DochatDisplayConfig;
  auth: DochatAuth;
  locale: string;
  // The `dialonce-login` query parameter: staff get the Dial Once sign-in back.
  forceDialOnceLogin: boolean;
  onTicket: (ticket: string) => void;
  // Where the fonts of the chat's theme are served from.
  cdnUrl: string;
};

const cn = (...classNames: (string | false | undefined)[]) => classNames.filter(Boolean).join(" ");

// The two shapes of the v2 chat's message buttons, driven by its theme variables.
const buttonClassNames = {
  primary: cn(
    "bg-(--message-button-cta-background)/(--message-button-cta-background-opacity)",
    "hover:bg-(--message-button-cta-hover-background)/(--message-button-cta-hover-background-opacity)",
    "text-(--message-button-cta-foreground)/(--message-button-cta-foreground-opacity)",
    "hover:text-(--message-button-cta-hover-foreground)/(--message-button-cta-hover-foreground-opacity)",
    "border-(length:--message-button-cta-border-width)",
    "border-(--message-button-cta-border-color)/(--message-button-cta-border-opacity)",
    "rounded-(--message-button-cta-border-radius)",
    "font-(--message-button-cta-font-weight) font-(family-name:--message-button-cta-font-family)",
    "text-(length:--message-button-cta-font-size)",
    "min-h-(--message-button-cta-height)",
    "[box-shadow:var(--message-button-cta-box-shadow)]"
  ),
  secondary: cn(
    "bg-(--message-button-background)/(--message-button-background-opacity)",
    "hover:bg-(--message-button-hover-background)/(--message-button-hover-background-opacity)",
    "text-(--message-button-foreground)/(--message-button-foreground-opacity)",
    "hover:text-(--message-button-hover-foreground)/(--message-button-hover-foreground-opacity)",
    "border-(length:--message-button-border-width)",
    "border-(--message-button-border-color)/(--message-button-border-opacity)",
    "rounded-(--message-button-border-radius)",
    "font-(--message-button-font-weight) font-(family-name:--message-button-font-family)",
    "text-(length:--message-button-font-size)",
    "min-h-(--message-button-height)",
    "[box-shadow:var(--message-button-box-shadow)]"
  ),
};

const Notice = ({ tone, children }: { tone: "error" | "info"; children: ReactNode }) => (
  <div
    className={cn(
      "w-full rounded-(--radius-sm) border border-solid px-3 py-2 text-(length:--texts-font-size)",
      tone === "error"
        ? "border-(--error-color) text-(--error-color)"
        : "border-(--border-color) text-(--texts-color)"
    )}
    role={tone === "error" ? "alert" : "status"}
  >
    {children}
  </div>
);

const ProviderButton = ({
  provider,
  label,
  dochatConfigId,
  locale,
  authUrl,
  onTicket,
  variant,
}: {
  provider: DochatAuthProvider;
  label: string;
  dochatConfigId: string;
  locale: string;
  authUrl: string | null;
  onTicket: (ticket: string) => void;
  variant: keyof typeof buttonClassNames;
}) => {
  const texts = getAuthTexts(locale);
  const { state, open } = useSignIn({ dochatConfigId, provider, locale, authUrl, onTicket });

  return (
    <div className="flex flex-col gap-2">
      <Button
        className={cn(
          "h-auto w-full cursor-pointer border-solid px-4 py-2 whitespace-normal",
          "focus-visible:ring-(--primary-color)/50",
          buttonClassNames[variant]
        )}
        variant="none"
        onClick={open}
      >
        {label}
      </Button>
      {state.step === "waiting" && (
        <p className="m-0 text-(length:--texts-font-size) text-(--texts-color)">
          {texts.waiting}
          {state.showDifferentUserHint && (
            <>
              <br />
              {texts.differentUserHint}
            </>
          )}
        </p>
      )}
      {state.step === "popup_blocked" && (
        <Notice tone="info">
          {texts.popupBlocked}{" "}
          <a className="underline" href={window.location.href} rel="noopener" target="_blank">
            {texts.openInNewTab}
          </a>
        </Notice>
      )}
      {state.step === "failed" && (
        <Notice tone="error">
          {texts.reason(state.reason)}
          {state.reference && (
            <span className="mt-1 block text-(length:--font-size-xs)">
              {texts.reference(state.reference)}
            </span>
          )}
        </Notice>
      )}
    </div>
  );
};

/**
 * The login screen of a chat that requires sign-in, shown in the chat's own theme once the
 * configuration answer said so. Sign-in runs in a popup: the chat usually sits in a cross-site
 * iframe, where identity-provider pages cannot be shown.
 */
export const LoginScreen = ({
  dochatConfigId,
  config,
  auth,
  locale,
  forceDialOnceLogin,
  onTicket,
  cdnUrl,
}: LoginScreenProps) => {
  const texts = getAuthTexts(locale);
  const { chatbotParams } = config;
  const localized = chatbotParams.availableLocales?.[locale];
  const name = localized?.title || chatbotParams.title || "";
  // Like the chat's header. The logo is made for that header, so it is shown on the header's
  // background; with the full logo, the name stays for assistive technologies only.
  const showFullLogo = chatbotParams.logoType === "full" && Boolean(chatbotParams.fullLogoDataUri);
  const logo = showFullLogo ? chatbotParams.fullLogoDataUri : chatbotParams.logoDataUri;
  const logoImage = logo && (
    <img
      alt=""
      // A set height: an SVG with only a viewBox has no size of its own to give.
      className="h-16 w-auto max-w-full object-contain"
      src={logo}
    />
  );

  const providers = new Set(auth.providers);
  if (forceDialOnceLogin) providers.add("dialonce");

  return (
    <div
      className={cn(
        "flex min-h-screen flex-col items-center justify-center gap-6 p-6",
        "bg-(--background-color)/(--background-color-opacity) text-(--texts-color)",
        "font-(family-name:--texts-font-family)"
      )}
    >
      <CSSVariables chatbotParams={{ theme: chatbotParams.theme }} />
      <Fonts cdnUrl={cdnUrl} chatbotParams={chatbotParams} />
      {logoImage && (
        <div
          className={cn(
            "flex max-w-full items-center justify-center px-5 py-3",
            "rounded-(--radius-md) bg-(--header-background)"
          )}
        >
          {logoImage}
        </div>
      )}
      <h1
        className={cn(
          "m-0 flex flex-col items-center gap-1 text-center",
          "font-(family-name:--titles-font-family) text-(--titles-color)"
        )}
      >
        {name && (
          <span
            className={
              showFullLogo ? "sr-only" : "text-(length:--font-size-xl) font-(--titles-font-weight)"
            }
          >
            {name}
          </span>
        )}
        <span className="text-(length:--font-size-lg) font-normal">{texts.title}</span>
      </h1>

      {auth.reason && (
        <div className="w-full max-w-90">
          <Notice tone={auth.status === "forbidden" ? "error" : "info"}>
            {texts.reason(auth.reason)}
          </Notice>
        </div>
      )}

      <div className="flex w-full max-w-90 flex-col gap-3">
        {providers.has("customer") && (
          <ProviderButton
            authUrl={auth.authUrl}
            dochatConfigId={dochatConfigId}
            label={texts.signInWithCompany(auth.customerProviderName || name)}
            locale={locale}
            provider="customer"
            variant="primary"
            onTicket={onTicket}
          />
        )}
        {providers.has("dialonce") && (
          <ProviderButton
            authUrl={auth.authUrl}
            dochatConfigId={dochatConfigId}
            label={texts.signInWithDialOnce}
            locale={locale}
            provider="dialonce"
            variant={providers.has("customer") ? "secondary" : "primary"}
            onTicket={onTicket}
          />
        )}
      </div>

      {auth.status === "forbidden" && auth.authUrl && (
        <Button
          className="cursor-pointer text-(length:--texts-font-size) text-(--texts-color) underline"
          variant="none"
          onClick={() => window.open(`${auth.authUrl}/auth/signout`, "dochat-auth", "popup")}
        >
          {texts.signOutAndUseAnother}
        </Button>
      )}
    </div>
  );
};
